/**
 * Generates a small card-shaped thumbnail (160×100px JPEG)
 * for cart display. Shared between Product page and CartEditModal.
 */
export async function generateCardThumbnail(
  templateImageUrl: string,
  photo: string | null,
  photoPosition: { x: number; y: number; width: number; height: number } | null
): Promise<string | null> {
  try {
    const THUMBNAIL_WIDTH = 160;
    const THUMBNAIL_HEIGHT = Math.round(THUMBNAIL_WIDTH / 1.6);

    const canvas = document.createElement('canvas');
    canvas.width = THUMBNAIL_WIDTH;
    canvas.height = THUMBNAIL_HEIGHT;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Load template image
    const templateImg = new Image();
    templateImg.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      templateImg.onload = () => resolve();
      templateImg.onerror = () => reject(new Error('Failed to load template image'));
      templateImg.src = templateImageUrl;
    });

    // Draw template background (cover fit)
    const templateAspect = templateImg.naturalWidth / templateImg.naturalHeight;
    const canvasAspect = THUMBNAIL_WIDTH / THUMBNAIL_HEIGHT;
    let sx = 0, sy = 0, sw = templateImg.naturalWidth, sh = templateImg.naturalHeight;

    if (templateAspect > canvasAspect) {
      sw = templateImg.naturalHeight * canvasAspect;
      sx = (templateImg.naturalWidth - sw) / 2;
    } else {
      sh = templateImg.naturalWidth / canvasAspect;
      sy = (templateImg.naturalHeight - sh) / 2;
    }

    ctx.drawImage(templateImg, sx, sy, sw, sh, 0, 0, THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT);

    // Draw user photo at the correct position
    if (photo && photoPosition) {
      const photoImg = new Image();
      photoImg.crossOrigin = 'anonymous';
      await new Promise<void>((resolve, reject) => {
        photoImg.onload = () => resolve();
        photoImg.onerror = () => reject(new Error('Failed to load photo'));
        photoImg.src = photo;
      });

      const photoX = (photoPosition.x / 100) * THUMBNAIL_WIDTH;
      const photoY = (photoPosition.y / 100) * THUMBNAIL_HEIGHT;
      const photoW = (photoPosition.width / 100) * THUMBNAIL_WIDTH;
      const photoH = (photoPosition.height / 100) * THUMBNAIL_HEIGHT;

      const imgAspect = photoImg.naturalWidth / photoImg.naturalHeight;
      const targetAspect = photoW / photoH;
      let psx = 0, psy = 0, psw = photoImg.naturalWidth, psh = photoImg.naturalHeight;

      if (imgAspect > targetAspect) {
        psw = photoImg.naturalHeight * targetAspect;
        psx = (photoImg.naturalWidth - psw) / 2;
      } else {
        psh = photoImg.naturalWidth / targetAspect;
        psy = (photoImg.naturalHeight - psh) / 2;
      }

      ctx.drawImage(photoImg, psx, psy, psw, psh, photoX, photoY, photoW, photoH);
    }

    return canvas.toDataURL('image/jpeg', 0.5);
  } catch (error) {
    console.error('Thumbnail generation failed:', error);
    return null;
  }
}
